/**
 * Push's real half: reading the actual browser to answer `PushEnvironment`,
 * and the two actions — subscribe, unsubscribe — that change what it would
 * answer. `src/lib/push.ts` is the rule (`availability`, `describePush`,
 * `notificationFor`) with none of this in it, tested without a browser at
 * all; this is the one place that touches `Notification`, `navigator` and
 * the service worker registration, the same split `src/theme-document.ts`
 * and `src/motion-document.ts` make for their own questions.
 */

import { applicationServerKey, type PushEnvironment } from './lib/push'

/** iOS and iPadOS, the one platform where install comes before permission. */
function isIos(): boolean {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua)) return true
  // iPadOS 13+ reports as a Mac; the tell is a Mac that can be touched.
  return ua.includes('Macintosh') && navigator.maxTouchPoints > 1
}

/** Running from the Home Screen, not a Safari or Chrome tab. */
function isInstalled(): boolean {
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true
  // iOS's own, older answer to the same question — still the reliable one there.
  return (navigator as { standalone?: boolean }).standalone === true
}

async function existingSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const registration = await navigator.serviceWorker.getRegistration()
  if (!registration) return null
  return registration.pushManager.getSubscription()
}

/** Whether this deployment has VAPID keys at all — `GET /api/push/vapid-key`. */
async function fetchConfigured(): Promise<{ configured: boolean; key: string | null }> {
  try {
    const res = await fetch('/api/push/vapid-key')
    if (!res.ok) return { configured: false, key: null }
    const body = (await res.json()) as { configured: boolean; key: string | null }
    return body
  } catch {
    return { configured: false, key: null }
  }
}

/** Reads the actual browser. Async because "is there a subscription already" is. */
export async function readPushEnvironment(): Promise<PushEnvironment> {
  const serviceWorker = 'serviceWorker' in navigator
  const pushManager = 'PushManager' in window
  const notification = 'Notification' in window
  const [{ configured }, subscription] = await Promise.all([
    fetchConfigured(),
    existingSubscription(),
  ])

  return {
    serviceWorker,
    pushManager,
    notification,
    permission: notification ? Notification.permission : null,
    installed: isInstalled(),
    ios: isIos(),
    subscribed: subscription !== null,
    configured,
  }
}

/**
 * Asks, subscribes, and tells the server. Throws the browser's own refusal
 * (denied permission, no registration yet) rather than swallowing it — the
 * card that calls this is what turns that into a sentence.
 */
export async function subscribeToPush(): Promise<void> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') throw new Error('notifications were not allowed')

  const registration = await navigator.serviceWorker.ready
  const { key } = await fetchConfigured()
  if (!key) throw new Error('push is not configured on this deployment')

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    // `as BufferSource`: TS's DOM lib is newly strict about `Uint8Array`'s
    // backing buffer being exactly `ArrayBuffer` rather than the broader
    // `ArrayBufferLike` every `Uint8Array` actually has; the object handed
    // to `subscribe` is a real typed array either way.
    applicationServerKey: applicationServerKey(key) as BufferSource,
  })
  const json = subscription.toJSON()

  const res = await fetch('/api/push/subscription', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    }),
  })
  if (!res.ok) throw new Error('the server did not accept the subscription')
}

/** Unsubscribes locally and tells the server, best-effort either way. */
export async function unsubscribeFromPush(): Promise<void> {
  const registration = await navigator.serviceWorker.getRegistration()
  const subscription = await registration?.pushManager.getSubscription()
  if (!subscription) return

  const endpoint = subscription.endpoint
  await subscription.unsubscribe()
  await fetch('/api/push/subscription', {
    method: 'DELETE',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  }).catch(() => {
    /* The browser has already forgotten it; a stale server row costs nothing
       but a wasted send the next time this account is notified, and that
       send simply deletes it then — see `server/push.ts`'s `notifyMany`. */
  })
}
